import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import Select from "react-select"; // Keep react-select for multi-select ease
import { MapContainer, TileLayer, Circle, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Import Cosailor UI Components
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
// Removed: import { Slider } from "./ui/slider"; // REMOVED - Slider component doesn't exist in ui/
import { Text } from "./catalyst/text";
import {
  X,
  Maximize2,
  Minimize2,
  RefreshCcw,
} from "lucide-react";

// Fix for default marker icons (keep this)
const defaultIcon = L.icon({
  iconUrl: "/marker-icon.png",
  shadowUrl: "/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = defaultIcon;

// Map component for geographic filtering (Leaflet remains)
function GeographicFilterMap({ center, radius, onChange }) {
  const defaultCenter = center || [39.8283, -98.5795];
  const mapRef = React.useRef(null);

  function MapClickHandler() {
    useMapEvents({
      click: (e) => {
        onChange(e.latlng);
      }, // Pass leaflet latlng object
    });
    return null;
  }

  useEffect(() => {
    if (mapRef.current) {
      if (center) {
        mapRef.current.flyTo(
          [center.lat, center.lng],
          mapRef.current.getZoom(),
        );
      } else {
        mapRef.current.flyTo([39.8283, -98.5795], 4);
      }
    }
  }, [center]);

  return (
    <div className="mt-4 border rounded-lg overflow-hidden">
      <MapContainer
        center={defaultCenter}
        zoom={4}
        style={{ height: "300px", width: "100%" }}
        ref={mapRef}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
        />
        <MapClickHandler />
        {center && radius > 0 && (
          <Circle
            center={[center.lat, center.lng]}
            radius={radius * 1609.34}
            pathOptions={{ fillColor: "blue", fillOpacity: 0.2, color: "blue" }}
          />
        )}
      </MapContainer>
      <Text className="text-xs text-muted-foreground text-center p-2 bg-muted/50">
        Click map to set center point.
      </Text>
    </div>
  );
}

// Main Filter Panel component
const FilterPanel = ({
  filterOptions,
  selectedFilters,
  onChange,
  onApply,
  baseUrl,
  token,
  customMode = false,
  onOpportunitiesSelected,
  buttonText,
}) => {
  const [localFilters, setLocalFilters] = useState(() => {
      // Get today's date and 7 days from now
      const today = new Date();
      const endDate = new Date(today.getTime() + (7 * 24 * 60 * 60 * 1000));
      
      // Define the default structure of localFilters, including new date fields
      const defaultStructure = {
        regions: [],
        markets: [],
        states: [],
        center: null,
        radius: 0,
        clustering_params: { eps: 0.0019, min_samples: 12 },
        startDate: today.toISOString().split('T')[0], // Format as YYYY-MM-DD
        endDate: endDate.toISOString().split('T')[0], // Format as YYYY-MM-DD
      };

      // Merge with selectedFilters prop, ensuring only known fields are brought in
      const initialFilters = { ...defaultStructure };
      if (selectedFilters) {
        for (const key in defaultStructure) {
          if (selectedFilters.hasOwnProperty(key)) {
            initialFilters[key] = selectedFilters[key];
          }
        }
        // Specifically handle new date fields from selectedFilters if they exist
        if (selectedFilters.hasOwnProperty('startDate')) {
            initialFilters.startDate = selectedFilters.startDate;
        }
        if (selectedFilters.hasOwnProperty('endDate')) {
            initialFilters.endDate = selectedFilters.endDate;
        }
      }
      // Ensure fields removed from UI are not carried over if they happen to be in selectedFilters
      delete initialFilters.priority_buckets;
      delete initialFilters.customer_priorities;
      return initialFilters;
    });
  const [uniqueStopsCount, setUniqueStopsCount] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const debounceTimeoutRef = useRef(null);

  // New state for opportunity selection
  const [opportunities, setOpportunities] = useState([]);
  const [selectedOpportunitiesIds, setSelectedOpportunitiesIds] = useState([]);
  const [isLoadingOpportunities, setIsLoadingOpportunities] = useState(false);

  // Update local state if props change
  useEffect(() => {
      setLocalFilters(prevLocalFilters => {
        // Get today's date and 7 days from now for defaults
        const today = new Date();
        const defaultEndDate = new Date(today.getTime() + (7 * 24 * 60 * 60 * 1000));
        
        // Start with a base of what localFilters should look like (including defaults for new fields)
        const baseStructure = {
          regions: [],
          markets: [],
          states: [],
          center: null,
          radius: 0,
          clustering_params: prevLocalFilters.clustering_params || { eps: 0.0019, min_samples: 12 }, // Preserve local clustering changes
          startDate: prevLocalFilters.startDate || today.toISOString().split('T')[0], // Preserve local date changes unless overridden by prop
          endDate: prevLocalFilters.endDate || defaultEndDate.toISOString().split('T')[0],   // Preserve local date changes unless overridden by prop
        };

        // Merge selectedFilters prop into this base structure
        const newFilters = { ...baseStructure };
        if (selectedFilters) {
          for (const key in baseStructure) { // Iterate over known keys in baseStructure
            if (selectedFilters.hasOwnProperty(key)) {
              newFilters[key] = selectedFilters[key];
            }
          }
          // Specifically handle date fields from props
           if (selectedFilters.hasOwnProperty('startDate')) {
            newFilters.startDate = selectedFilters.startDate;
          }
          if (selectedFilters.hasOwnProperty('endDate')) {
            newFilters.endDate = selectedFilters.endDate;
          }
        }
        // Ensure removed fields are not part of the state
        delete newFilters.priority_buckets;
        delete newFilters.customer_priorities;
        return newFilters;
      });
    }, [selectedFilters]);

  // Log filterOptions prop when component receives it
  useEffect(() => {
    console.log("FilterPanel received filterOptions prop:", filterOptions);
  }, [filterOptions]);

  // Fetch unique stops count function
  const fetchUniqueStopsCount = useCallback(
    async (filtersToPreview) => {
      if (!baseUrl || !token) return;

      setIsPreviewLoading(true);
      setUniqueStopsCount(null);

      const requestBody = { filters: {} };
      // Use the same filter population logic as fetchPreviewCount
      const filterKeys = ["regions", "markets", "states"];
      filterKeys.forEach((key) => {
        if (filtersToPreview[key] && filtersToPreview[key].length > 0) {
          requestBody.filters[key] = filtersToPreview[key];
        }
      });
      // Include date filters
      if (
        filtersToPreview.startDate !== null &&
        filtersToPreview.startDate !== undefined
      ) {
        requestBody.filters.start_date = filtersToPreview.startDate;
      }
      if (
        filtersToPreview.endDate !== null &&
        filtersToPreview.endDate !== undefined
      ) {
        requestBody.filters.end_date = filtersToPreview.endDate;
      }
      if (filtersToPreview.center && filtersToPreview.radius > 0) {
        requestBody.filters.center = filtersToPreview.center;
        requestBody.filters.radius = filtersToPreview.radius;
      }
      try {
        console.log("Fetching unique stops count with filters:", requestBody);
        const response = await axios.post(
          `${baseUrl}/api/v2/stops/filter`,
          requestBody,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        console.log("Unique stops count response:", response.data);
        if (
          response.data &&
          typeof response.data.total_stops === "number"
        ) {
          setUniqueStopsCount(response.data.total_stops);
          console.log(
            "Set unique stops count to:",
            response.data.total_stops,
          );
        } else {
          console.warn(
            "Invalid unique stops count response format:",
            response.data,
          );
          setUniqueStopsCount(null);
        }
      } catch (error) {
        console.error("Error fetching unique stops count:", error);
        setUniqueStopsCount(null);
      } finally {
        setIsPreviewLoading(false);
      }
    },
    [baseUrl, token],
  );

  // Effect to trigger debounced fetch on local filter changes
  useEffect(() => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(() => {
      fetchUniqueStopsCount(localFilters);
    }, 750);
    return () => clearTimeout(debounceTimeoutRef.current);
  }, [localFilters, fetchUniqueStopsCount]);

  // Format options for react-select
  const formatOptions = (options) => {
    console.log("Before formatOptions (regions):", options);
    const formatted = (options || []).sort().map((o) => ({ value: o, label: o }));
    console.log("After formatOptions (regions):", formatted);
    return formatted;
  }

  // Handle changes for react-select
  const handleMultiSelectChange = (field, selected) => {
    const newFilters = {
      ...localFilters,
      [field]: selected ? selected.map((item) => item.value) : [],
    };
    setLocalFilters(newFilters);
    onChange(newFilters);
  };

  // When selected regions change, reset the markets
  useEffect(() => {
    // This effect is now simplified: it just clears markets when regions change.
    // The parent component (`GeneratePage`) is responsible for re-fetching filter options.
    const newFilters = {
      ...localFilters,
      markets: [],
    };
      
    // Check if a region is selected and if markets are not already empty
    if (localFilters.regions?.length > 0 && localFilters.markets?.length > 0) {
      setLocalFilters(newFilters);
      onChange(newFilters);
    }
  }, [localFilters.regions]);

  // Handle changes for clustering parameters sliders (using regular input range for now)
  const handleClusterParamsChange = (param, value) => {
    const numericValue = parseFloat(value);
    const defaultValue = param === "eps" ? 0.0019 : 12;
    const newValue = isNaN(numericValue) ? defaultValue : numericValue;

    const newFilters = {
      ...localFilters,
      clustering_params: {
        ...(localFilters.clustering_params || { eps: 0.0019, min_samples: 12 }),
        [param]: newValue,
      },
    };
    setLocalFilters(newFilters);
    onChange(newFilters); // Note: clustering params don't trigger preview count fetch
  };

  // Geographic filter handlers
  const handleCenterChange = (centerPoint) => {
    const newRadius = centerPoint === null ? 0 : localFilters.radius || 0;
    const newFilters = {
      ...localFilters,
      center: centerPoint,
      radius: newRadius,
    };
    setLocalFilters(newFilters);
    onChange(newFilters);
  };

  const handleRadiusChange = (e) => {
    const radiusValue = parseFloat(e.target.value);
    const newFilters = {
      ...localFilters,
      radius: isNaN(radiusValue) || radiusValue < 0 ? 0 : radiusValue,
    };
    setLocalFilters(newFilters);
    onChange(newFilters);
  };

  // Handle changes for date inputs
  const handleDateChange = (field, value) => {
    console.log(`Date changed (${field}):`, value);
    
    // Always store valid date strings as is, never convert to null unless explicitly empty
    const newFilters = {
      ...localFilters,
      [field]: value === "" ? null : value, // Keep date as string in YYYY-MM-DD format
    };
    
    // Log the updated filters
    console.log(`Updated filters (${field}):`, newFilters[field]);
    
    setLocalFilters(newFilters);
    // Ensure dates are properly passed to parent component
    onChange(newFilters);
  };

  // Reset handler
  const handleResetFilters = () => {
    // Get today's date and 7 days from now for reset defaults
    const today = new Date();
    const defaultEndDate = new Date(today.getTime() + (7 * 24 * 60 * 60 * 1000));
    
    const resetFilters = {
      regions: [],
      states: [],
      markets: [],
      // priority_buckets: [], // Removed
      // customer_priorities: [], // Removed
      startDate: today.toISOString().split('T')[0], // Reset to today
      endDate: defaultEndDate.toISOString().split('T')[0], // Reset to 7 days from today
      center: null,
      radius: 0,
      clustering_params: { eps: 0.0019, min_samples: 12 },
    };
    setLocalFilters(resetFilters);
    onChange(resetFilters);
    fetchUniqueStopsCount(resetFilters); // Manually trigger preview fetch after reset
  };

  const renderSelectedValues = (values) => {
    if (!values || values.length === 0)
      return <span className="italic text-muted-foreground">Any</span>;
    if (values.length > 3)
      return (
        <>
          {values.slice(0, 3).join(", ")}... (+{values.length - 3})
        </>
      );
    return values.join(", ");
  };

  // React Select custom styles (basic theming) - Adjust as needed for dark mode
  const selectStyles = {
    control: (provided) => ({
      ...provided,
      borderColor: "hsl(var(--border))",
      backgroundColor: "hsl(var(--background))",
      boxShadow: "none",
      "&:hover": { borderColor: "hsl(var(--ring))" },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "hsl(var(--popover))",
      color: "hsl(var(--popover-foreground))",
      zIndex: 20,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "hsl(var(--primary))"
        : state.isFocused
          ? "hsl(var(--accent))"
          : "hsl(var(--popover))",
      color: state.isSelected
        ? "hsl(var(--primary-foreground))"
        : "hsl(var(--popover-foreground))",
      ":active": {
        backgroundColor: !state.isDisabled
          ? state.isSelected
            ? "hsl(var(--primary))"
            : "hsl(var(--accent))"
          : undefined,
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "hsl(var(--secondary))",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "hsl(var(--secondary-foreground))",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "hsl(var(--secondary-foreground))",
      ":hover": {
        backgroundColor: "hsl(var(--destructive))",
        color: "hsl(var(--destructive-foreground))",
      },
    }),
    input: (provided) => ({ ...provided, color: "hsl(var(--foreground))" }),
    placeholder: (provided) => ({
      ...provided,
      color: "hsl(var(--muted-foreground))",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "hsl(var(--foreground))",
    }),
    menuPortal: base => ({ ...base, zIndex: 9999 })
  };

  // Fetch opportunities when filters change in custom mode
  useEffect(() => {
    if (customMode && baseUrl) {
      fetchOpportunities(localFilters);
    }
  }, [customMode, localFilters, baseUrl]);

  // Function to fetch opportunities based on filters
  const fetchOpportunities = async (filtersToUse) => {
    if (!baseUrl) return;
    setIsLoadingOpportunities(true);
    
    const requestBody = { filters: {} };
    // Simplified filter population
    const filterKeys = ["regions", "markets", "states"];
    filterKeys.forEach((key) => {
      if (filtersToUse[key] && filtersToUse[key].length > 0) {
        requestBody.filters[key] = filtersToUse[key];
      }
    });
    
    // Include date filters
    if (filtersToUse.startDate !== null && filtersToUse.startDate !== undefined) {
      requestBody.filters.start_date = filtersToUse.startDate;
    }
    if (filtersToUse.endDate !== null && filtersToUse.endDate !== undefined) {
      requestBody.filters.end_date = filtersToUse.endDate;
    }
    if (filtersToUse.center && filtersToUse.radius > 0) {
      requestBody.filters.center = filtersToUse.center;
      requestBody.filters.radius = filtersToUse.radius;
    }
    
    try {
      const response = await axios.post(
        `${baseUrl}/opportunities`, 
        requestBody
      );
      
      if (response.data && Array.isArray(response.data)) {
        setOpportunities(response.data);
      } else if (response.data && Array.isArray(response.data.data)) {
        setOpportunities(response.data.data);
      } else {
        setOpportunities([]);
      }
    } catch (error) {
      console.error("Error fetching opportunities:", error);
      setOpportunities([]);
    } finally {
      setIsLoadingOpportunities(false);
    }
  };

  // Handle selecting opportunities
  const handleOpportunitySelection = (opportunityId, isSelected) => {
    let newSelectedIds;
    
    if (isSelected) {
      newSelectedIds = [...selectedOpportunitiesIds, opportunityId];
    } else {
      newSelectedIds = selectedOpportunitiesIds.filter(id => id !== opportunityId);
    }
    
    setSelectedOpportunitiesIds(newSelectedIds);
    
    // Find the full opportunity objects for the selected IDs
    const selectedOpportunityObjects = opportunities.filter(opp => 
      newSelectedIds.includes(opp.id || opp.opportunity_id)
    );
    
    // Call the callback with the selected opportunities
    if (onOpportunitiesSelected) {
      onOpportunitiesSelected(selectedOpportunityObjects);
    }
  };

  return (
    <div className="space-y-6">

      {/* Basic Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="regions-select">Regions</Label>
          <Select
            id="regions-select"
            isMulti
            name="regions"
            styles={selectStyles}
            menuPortalTarget={document.body}
            options={formatOptions(filterOptions?.regions)}
            value={
              localFilters.regions?.map((r) => ({ value: r, label: r })) || []
            }
            onChange={(selected) =>
              handleMultiSelectChange("regions", selected)
            }
            placeholder="Select regions..."
            isDisabled={!filterOptions?.regions}
            aria-label="Select Regions Filter"
          />
          <div className="text-xs text-muted-foreground mt-1 h-4">
            {renderSelectedValues(localFilters.regions)}
          </div>
        </div>
        <div>
          <Label htmlFor="markets-select">Markets</Label>
          <Select
            id="markets-select"
            isMulti
            name="markets"
            styles={selectStyles}
            menuPortalTarget={document.body} // <--- ADDED THIS LINE
            options={formatOptions(filterOptions?.markets)}
            value={
              localFilters.markets?.map((m) => ({ value: m, label: m })) || []
            }
            onChange={(selected) =>
              handleMultiSelectChange("markets", selected)
            }
            placeholder="Select markets..."
            isDisabled={!filterOptions?.markets}
            aria-label="Select Markets Filter"
          />
          <div className="text-xs text-muted-foreground mt-1 h-4">
            {renderSelectedValues(localFilters.markets)}
          </div>
        </div>
      </div>

      {/* Date Filters and Customer Count */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
        <div>
          <Label htmlFor="start-date-input" className="mb-1 block text-sm">Start Date</Label>
          <Input
            id="start-date-input"
            type="date"
            value={localFilters.startDate || ""}
            onChange={(e) => handleDateChange("startDate", e.target.value)}
            className="h-9 w-full"
            aria-label="Select Start Date"
          />
          <div className="text-xs text-muted-foreground mt-1 h-4">
            {/* Optional: Display selected date or help text */}
          </div>
        </div>

        <div>
          <Label htmlFor="end-date-input" className="mb-1 block text-sm">End Date</Label>
          <Input
            id="end-date-input"
            type="date"
            value={localFilters.endDate || ""}
            onChange={(e) => handleDateChange("endDate", e.target.value)}
            className="h-9 w-full"
            aria-label="Select End Date"
          />
          <div className="text-xs text-muted-foreground mt-1 h-4">
            {/* Optional: Display selected date or help text */}
          </div>
        </div>

        <div>
          <Label htmlFor="min-customers-input" className="mb-1 block text-sm">Min Customers/Schedule</Label>
          <Input
            id="min-customers-input"
            type="number"
            min="1"
            max="30"
            value={localFilters.clustering_params?.min_samples ?? 12}
            onChange={(e) => handleClusterParamsChange("min_samples", e.target.value)}
            className="h-9 w-full"
            aria-label="Minimum Customers per Schedule"
          />
          <div className="text-xs text-muted-foreground mt-1 h-4">
            {/* Optional: Display help text */}
          </div>
        </div>
      </div>

      {/* Custom Mode Opportunities Section */}
      {customMode && (
        <div className="mt-6 border-t pt-4">
          <h3 className="text-lg font-medium mb-4">Select Opportunities</h3>
          
          {isLoadingOpportunities ? (
            <div className="text-center py-4">Loading opportunities...</div>
          ) : opportunities.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              No opportunities found with current filters. Try adjusting your filters.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground mb-2">
                {selectedOpportunitiesIds.length} of {opportunities.length} opportunities selected
              </div>
              
              <div className="border rounded-md overflow-hidden">
                <div className="max-h-[400px] overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Select
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Region
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Market
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Priority
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                      {opportunities.map((opportunity) => {
                        const id = opportunity.id || opportunity.opportunity_id;
                        return (
                          <tr 
                            key={id} 
                            className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                            onClick={() => handleOpportunitySelection(
                              id, 
                              !selectedOpportunitiesIds.includes(id)
                            )}
                          >
                            <td className="px-4 py-3 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedOpportunitiesIds.includes(id)}
                                onChange={(e) => handleOpportunitySelection(id, e.target.checked)}
                                className="h-4 w-4 text-primary border-gray-300 rounded"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {opportunity.name || opportunity.customer_name || 'Unnamed'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {opportunity.region || 'N/A'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {opportunity.market || 'N/A'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {opportunity.priority || 'Normal'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}



      {/* Geographic Filter */}
      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-medium text-sm">Geographic Filter (Optional)</h4>
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="flex items-center gap-2">
            <Label htmlFor="radius-input" className="text-sm">
              Radius:
            </Label>
            <Input
              id="radius-input"
              type="number"
              min="0"
              max="1000"
              value={localFilters.radius || ""}
              onChange={handleRadiusChange}
              placeholder="Miles"
              disabled={!localFilters.center}
              aria-label="Radius in miles"
              className="w-24 h-9" // Adjust size
            />
            <Label
              htmlFor="radius-input"
              className="text-sm text-muted-foreground"
            >
              miles
            </Label>
          </div>
          {localFilters.center && (
            <div className="flex items-center gap-2 text-sm">
              <span>
                Center: {localFilters.center.lat.toFixed(4)},{" "}
                {localFilters.center.lng.toFixed(4)}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCenterChange(null)}
                className="text-destructive hover:text-destructive px-1 h-auto"
              >
                <X className="mr-1 h-3 w-3" /> Clear
              </Button>
            </div>
          )}
          {!localFilters.center && (
            <Text className="text-sm italic text-muted-foreground">
              Click map below to set center
            </Text>
          )}
        </div>
        <GeographicFilterMap
          center={localFilters.center}
          radius={localFilters.radius}
          onChange={handleCenterChange}
        />
      </div>

      {/* Bottom Controls Section */}
      <div className="flex justify-between items-center pt-4 border-t">
        <div className="text-sm space-y-1">
          {isPreviewLoading ? (
            <span className="text-muted-foreground">Calculating matches...</span>
          ) : (
            <div className="space-y-1">
              {uniqueStopsCount !== null && (
                <div className="text-primary">
                  <span className="font-medium">{uniqueStopsCount}</span> unique
                  customer {uniqueStopsCount === 1 ? "stop" : "stops"}{" "}
                  available.
                </div>
              )}
              {uniqueStopsCount === null && (
                <span className="text-muted-foreground">
                  Use filters to find opportunities.
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end space-y-2">
          <Button onClick={onApply} disabled={isPreviewLoading}>
            {buttonText || (customMode ? "Apply Filters" : "Generate Schedules")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;
